import React, { useState, useEffect, useRef } from 'react';
import { PANE_WIDE_WIDTH } from '../constants/paneLayout';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { colors, spacing } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useChatStore } from '../store/chatStore';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService } from '../services/api';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { socketService } from '../services/socket.service';
import { Header, ScreenLayout, FloatingActionButton, WhatsAppContactModal, MoreOptionsModal } from '../components';
import { ContactsList } from '../components/ContactsList';
import { Ionicons } from '@expo/vector-icons';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useDesktopPaneLayout } from '../hooks/useDesktopPaneLayout';
import { usePaneShellConfig } from '../components/PersistentPaneShell';
import { ConversationScreen } from './ConversationScreen';
import { CallHistoryScreen } from './CallHistoryScreen';
import { PaneHeader, PaneHeaderIconButton } from '../components/PaneHeader';
import { useContactConversationPane } from '../hooks/useContactConversationPane';
import { ContactEditForm, getChannelName } from '../components/ContactEditForm';
import { ConversationIdentifierDropdown } from '../components/ConversationIdentifierDropdown';

interface InboxScreenProps {
  /** False when this screen is mounted inside PersistentPaneShell but
   * currently hidden (another screen in the group is active) — see
   * usePaneShellConfig's own doc comment for why this must gate shell
   * registration. Always true outside the shell (native/narrow mobile-web). */
  isActive?: boolean;
  /** A contact id read from the URL's `?contact=` query param at app boot —
   * restores that contact's profile+conversation on page load/refresh. See
   * useContactConversationPane's openContactById. */
  deepLinkContactId?: string | null;
  /** Called once the deep-linked contact is open, so later visits start on the screen itself. */
  onDeepLinkContactOpened?: () => void;
}

/** The Inbox: the contacts list in its inbox mode (ContactsList mode="inbox") — same rows, order
 * and filters as Contacts, plus the inbox status filter and each contact's latest conversation.
 * Opening a row shows the contact's profile in place of the list and that exact conversation (or,
 * for a call, the call history) in the right pane / full screen on phones. */
export const InboxScreen: React.FC<InboxScreenProps> = ({ isActive = true, deepLinkContactId, onDeepLinkContactOpened }) => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab, startPhoneCall, isPhoneDialerReady } = useAppContext();
  const insets = useSafeAreaInsets();
  const conversationsRefreshToken = useChatStore((state) => state.conversationsRefreshToken);
  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const rtl = useRTLStyles();
  // Shared with AppNavigator (which routes 'chat' through PersistentPaneShell
  // on desktop web) and the other shell screens — must stay the one shared,
  // reactive hook so the two sides can never disagree.
  const isDesktopPaneLayout = useDesktopPaneLayout();

  // Bumped to reload the list for changes its own contact-update listener doesn't see (a
  // conversation read, a new message, a contact added from here); debounced so a burst of events
  // is one reload.
  const [inboxRefresh, setInboxRefresh] = useState(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefresh = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => setInboxRefresh((n) => n + 1), 600);
  };

  // FAB and modal states
  const [showWhatsAppContactModal, setShowWhatsAppContactModal] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);

  // The opened contact's profile (in place of the list) and conversation (right pane) — see
  // useContactConversationPane's own doc comment.
  const { viewedContact, conversationId: viewedConversationId, selectedChannel: conversationSelectedChannel, selectedIdentifierValue: conversationSelectedIdentifierValue, identifierOptions: conversationIdentifierOptions, switchConversationIdentifier, openContact: openContactConversation, openContactById, closeContact: closeContactConversation, callHistoryTarget, openCallHistory, closeCallHistory } = useContactConversationPane();

  // Restore a `?contact=<id>` deep link on page load/refresh — guarded on
  // !viewedContact so it never clobbers a contact the user already opened
  // (or changed) by clicking, and in practice only ever fires once.
  useEffect(() => {
    if (deepLinkContactId && !viewedContact) {
      openContactById(deepLinkContactId);
    }
    if (deepLinkContactId) onDeepLinkContactOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkContactId]);

  // FAB options
  const fabOptions = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: 'logo-whatsapp' as const,
      color: '#25D366',
      onPress: () => setShowWhatsAppContactModal(true),
    },
  ];

  // Handle contact added
  const handleContactAdded = (contact: { name: string; phoneNumber: string }) => {
    console.log('Contact added:', contact);
    showNotification('success', t('inbox.contactAdded'), t('inbox.contactAddedDesc', { name: contact.name }));
    setInboxRefresh((n) => n + 1);
  };

  // Handle conversation started
  const handleConversationStarted = (contact: { name: string; phoneNumber: string; topic: string }) => {
    console.log('Conversation started:', contact);
    showNotification('success', t('inbox.conversationStarted'), t('inbox.conversationStartedDesc', { name: contact.name }));
    setInboxRefresh((n) => n + 1);
  };

  useEffect(() => {
    setActiveTab('chat');
  }, [setActiveTab]);

  useEffect(() => {
    if (conversationsRefreshToken > 0) scheduleRefresh();
  }, [conversationsRefreshToken]);

  useEffect(() => {
    socketService.onNewMessage(scheduleRefresh);
    socketService.onConversationUpdate(scheduleRefresh);
    socketService.on('conversationMarkedAsRead', scheduleRefresh);
    return () => {
      socketService.offNewMessage();
      socketService.offConversationUpdate();
      socketService.off('conversationMarkedAsRead');
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, []);

  // A row: open the contact on its latest conversation (that exact one), or the call history when
  // the latest was a call; with no conversation loaded yet, on the channel last used.
  const handleInboxContactPress = (contact: any) => {
    const conversation = contact.inboxConversation;
    if (!conversation) {
      openContactConversation(contact);
      return;
    }
    if (conversation.channel === 'phone') {
      openContactConversation(contact, 'phone', conversation.phoneNumber);
      return;
    }
    openContactConversation(contact, conversation.channel, conversation.phoneNumber, conversation.contactId, conversation.id);
  };

  // Contact clicked in the left pane's contacts list — same as a row here.
  const handleContactRowPress = (contact: any) => {
    setActiveTab('contacts');
    openContactConversation(contact);
  };

  const handleCloseRightPane = () => {
    if (callHistoryTarget) {
      closeCallHistory();
    } else {
      closeContactConversation();
    }
  };

  const handleTabNavigation = createTabPressHandler(navigateTo, setActiveTab);
  const handleTabPress = (screenId: string) => {
    handleCloseRightPane();
    handleTabNavigation(screenId);
  };

  const header = (
    <Header
      showNotifications={true}
      showMessages={true}
      onNotificationPress={() => navigateTo('notifications')}
      onMessagePress={() => {}} // Already on inbox screen
      showNavigation={Platform.OS === 'web'}
      activeTab={activeTab}
      onTabPress={handleTabPress}
    />
  );

  // The clicked contact's conversation, embedded in the right pane — new,
  // actually-reachable replacement for this screen's old dead 'details' stub.
  const conversationRightPaneContent = viewedContact ? (
    <View style={{ flex: 1, height: '100%' }}>
      <PaneHeader
        titleContent={
          <ConversationIdentifierDropdown
            options={conversationIdentifierOptions}
            selectedChannel={conversationSelectedChannel}
            selectedValue={conversationSelectedIdentifierValue}
            formatChannelName={(channel) => getChannelName(channel, t)}
            onSelect={switchConversationIdentifier}
          />
        }
        trailing={<PaneHeaderIconButton icon="close" onPress={handleCloseRightPane} />}
      />
      {viewedConversationId ? (
        <ConversationScreen
          embedded
          conversationId={viewedConversationId}
          onNavigateBack={handleCloseRightPane}
          onViewContact={() => {}}
        />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}
    </View>
  ) : undefined;

  // A tapped phone identifier's Call History, same pane slot as the
  // conversation above (useContactConversationPane keeps the two mutually
  // exclusive) — takes priority over conversationRightPaneContent below.
  const handleCallHistoryCall = async () => {
    if (!callHistoryTarget || !isPhoneDialerReady) return;
    let target = callHistoryTarget.phoneNumber;
    try {
      const res = await apiService.getContactDialTarget(callHistoryTarget.contactId, undefined, undefined, undefined, callHistoryTarget.phoneIndex);
      target = res.data?.phoneNumber || target;
    } catch (error) {
      console.error('[InboxScreen] Failed to resolve phone call target', error);
    }
    if (!target) return;
    startPhoneCall(target, callHistoryTarget.contactId, { name: callHistoryTarget.contactName || target });
  };

  // Same header as the conversation above (identifier dropdown, so the user
  // can switch between this contact's numbers/channels) plus a call button.
  const callHistoryRightPaneContent = callHistoryTarget ? (
    <View style={{ flex: 1, height: '100%' }}>
      <PaneHeader
        titleContent={
          <ConversationIdentifierDropdown
            options={conversationIdentifierOptions}
            selectedChannel={conversationSelectedChannel}
            selectedValue={conversationSelectedIdentifierValue}
            formatChannelName={(channel) => getChannelName(channel, t)}
            onSelect={switchConversationIdentifier}
          />
        }
        trailing={
          <View style={{ flexDirection: rtl.rowDirection, alignItems: 'center', gap: spacing.sm }}>
            {!!callHistoryTarget.phoneNumber && (
              <TouchableOpacity style={styles.callHistoryPaneCallButton} onPress={handleCallHistoryCall} activeOpacity={0.85}>
                <Ionicons name="call" size={18} color="#FFFFFF" />
              </TouchableOpacity>
            )}
            <PaneHeaderIconButton icon="close" onPress={handleCloseRightPane} />
          </View>
        }
      />
      <CallHistoryScreen
        embedded
        hideEmbeddedHeader
        contactId={callHistoryTarget.contactId}
        contactName={callHistoryTarget.contactName}
        phoneNumber={callHistoryTarget.phoneNumber}
        phoneIndex={callHistoryTarget.phoneIndex}
        onClose={handleCloseRightPane}
      />
    </View>
  ) : undefined;

  const activeRightPaneContent = callHistoryRightPaneContent || conversationRightPaneContent;
  // Registered with the nearest PersistentPaneShell ancestor on desktop web;
  // a harmless no-op on native/narrow mobile-web, where this screen renders
  // its own <ScreenLayout> directly below instead.
  usePaneShellConfig({
    header,
    showBottomTabs: Platform.OS !== 'web',
    onTabPress: handleTabPress,
    onContactPress: handleContactRowPress,
    activeContactId: viewedContact?.id,
    onRightPaneClose: handleCloseRightPane,
    rightPaneContent: activeRightPaneContent ? 'custom' : 'empty',
    rightPaneCustomContent: activeRightPaneContent,
    rightPaneTab: callHistoryRightPaneContent ? 'calls' : 'chat',
    // Wider than the default pane width — a conversation needs more room,
    // same 480px convention PostsListScreen.tsx already uses for its own
    // "needs more room" case.
    rightPaneWidthOverride: activeRightPaneContent ? PANE_WIDE_WIDTH : undefined,
  }, isActive);

  const content = (
    <>
      {/* The opened contact's profile replaces the list, as before. */}
      {viewedContact ? (
        <View style={{ flex: 1 }}>
          <ContactEditForm
            contact={viewedContact}
            onClose={handleCloseRightPane}
            compact={true}
            onChannelSelect={(channel, identifierValue, channelContactId) => viewedContact && openContactConversation(viewedContact, channel, identifierValue, channelContactId)}
            onViewCallHistory={(contactId, contactName, phoneNumber, phoneIndex) => openCallHistory(contactId, contactName, phoneNumber, phoneIndex)}
          />
        </View>
      ) : null}
      {/* Kept mounted under the profile, so closing it returns to the same filters, pages and scroll. */}
      <View style={[styles.content, viewedContact ? { display: 'none' } : null]}>
        <ContactsList
          mode="inbox"
          showHeader={true}
          showSearch={true}
          showFilters={true}
          onContactPress={handleInboxContactPress}
          activeContactId={viewedContact?.id}
          refreshTrigger={inboxRefresh}
          scrollContentPaddingBottom={96}
        />
        <FloatingActionButton
          options={fabOptions}
          mainIcon="add"
          mainColor={colors.primary}
          position={isRTL ? 'bottom-left' : 'bottom-right'}
        />
      </View>

      {/* WhatsApp Contact Modal */}
      <WhatsAppContactModal
        visible={showWhatsAppContactModal}
        onClose={() => setShowWhatsAppContactModal(false)}
        onContactAdded={handleContactAdded}
        onConversationStarted={handleConversationStarted}
      />
    </>
  );

  const overlays = <MoreOptionsModal visible={showMoreModal} onClose={() => setShowMoreModal(false)} />;

  // Desktop web: AppNavigator routes this screen through PersistentPaneShell,
  // which owns the actual <ScreenLayout> — this screen just returns its
  // content/overlays for the shell to place. Native/narrow mobile-web: no
  // shell exists, so this screen renders its own <ScreenLayout> exactly as
  // every screen outside the shell group still does.
  if (isDesktopPaneLayout) {
    return (
      <>
        {content}
        {overlays}
      </>
    );
  }

  return (
    <>
      <ScreenLayout
        header={header}
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="contacts"
        onContactPress={handleContactRowPress}
        onRightPaneClose={handleCloseRightPane}
        rightPaneContent={activeRightPaneContent ? 'custom' : 'empty'}
        rightPaneCustomContent={activeRightPaneContent}
        rightPaneTab={callHistoryRightPaneContent ? 'calls' : 'chat'}
      >
        {content}
      </ScreenLayout>
      {overlays}
      {/* Phone layout: the desktop right pane's conversation / call-history
          view (identifier dropdown header + embedded conversation),
          full-screen — replaces the old standalone conversation and
          call-history screens on phones. */}
      {!isDesktopPaneLayout && activeRightPaneContent && (
        <View style={[styles.mobilePaneFullScreen, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
          {activeRightPaneContent}
        </View>
      )}
    </>
  );
};
const getStyles = () => StyleSheet.create({
  mobilePaneFullScreen: {
    ...(Platform.OS === 'web'
      ? { position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0 }
      : { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }),
    zIndex: 100000,
    backgroundColor: colors.background.surface,
  },
  callHistoryPaneCallButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    position: 'relative',
    // A flex:1 box on web otherwise grows to fit its content instead of being clamped to
    // ScreenLayout's bounded area, so the list never scrolls on its own.
    ...(Platform.OS === 'web' && { minHeight: 0 as any }),
  },
});
